import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  AlertTriangle,
  ArrowUpRight,
  CheckCircle2,
  FileText,
  Info,
  Link2,
  Paperclip,
} from "lucide-react";
import { requisitionBriefWorkspace } from "@/lib/api";
import {
  buildApprovalBrief,
  deriveRequirementStates,
} from "@/lib/product-intelligence.mjs";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { displayDate, money, titleCase } from "@/lib/utils";
import { toneForPassport, toneLabel } from "@/lib/control-passport-presentation";

type Props = {
  organisationId: string;
  requisitionId?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onNavigate: (view: string, recordId?: string) => void;
  canDecide?: boolean;
  onDecision?: (value: "approved" | "rejected") => void;
  onRequestClarification?: () => void;
};

export function RequisitionIntelligence({
  organisationId,
  requisitionId,
  open,
  onOpenChange,
  onNavigate,
  canDecide = false,
  onDecision,
  onRequestClarification,
}: Props) {
  const [data, setData] =
    useState<Awaited<ReturnType<typeof requisitionBriefWorkspace>>>();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !requisitionId) return;
    let live = true;
    setLoading(true);
    setError("");
    requisitionBriefWorkspace(organisationId, requisitionId)
      .then((value) => {
        if (live) setData(value);
      })
      .catch((reason: Error) => {
        if (live) setError(reason.message);
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [open, organisationId, requisitionId]);

  const brief = useMemo(
    () => (data?.requisition ? buildApprovalBrief(data) : null),
    [data],
  );
  const requirements = useMemo(
    () =>
      data?.requisition
        ? deriveRequirementStates(data.requisition, data.attachments)
        : [],
    [data],
  );
  const lowestQuote = useMemo(
    () => [...(data?.quotes || [])].sort((left, right) => Number(left.total) - Number(right.total))[0],
    [data],
  );
  const selectedQuote = useMemo(
    () => data?.quotes.find((quote: any) => quote.vendor_id === data.purchaseOrder?.vendor_id),
    [data],
  );
  const selectedVariance =
    selectedQuote && lowestQuote
      ? Number(selectedQuote.total) - Number(lowestQuote.total)
      : null;
  const controlCase = data?.controlPassport.case;
  const controlSnapshot = controlCase?.snapshots?.[0];
  const controlTone = toneForPassport(controlSnapshot, controlCase?.control_origin);
  const policyVersionId = controlSnapshot?.payload?.policy_version_id;
  const evaluatedPolicy = data?.controlPassport.policies
    .flatMap((item: any) =>
      (item.versions || []).map((version: any) => ({
        ...version,
        policyName: item.policy?.name,
      })),
    )
    .find((version: any) => version.id === policyVersionId);
  const committedBudget = (data?.budget?.budget_commitments || []).reduce(
    (sum: number, item: any) => sum + Number(item.amount || 0),
    0,
  );
  const availableBudget = data?.budget
    ? Number(data.budget.allocated_amount || 0) - committedBudget
    : null;

  function exportDecisionPack() {
    if (!data?.requisition || !brief) return;
    const pack = {
      kind: "RAQOZ_REQUIREMENT_AND_APPROVAL_PACK",
      generated_at: new Date().toISOString(),
      source_requisition_id: data.requisition.id,
      requirement_states: requirements,
      approval_brief: brief,
      recorded_approvals: data.approvals,
      attachment_references: data.attachments.map((item: any) => ({
        id: item.id,
        file_name: item.file_name,
        sha256: item.sha256,
      })),
      notice:
        "Deterministic export of current source records; it does not replace the immutable transaction audit trail.",
    };
    const url = URL.createObjectURL(
      new Blob([JSON.stringify(pack, null, 2)], { type: "application/json" }),
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = `${data.requisition.request_no}-decision-pack.json`;
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[min(1120px,calc(100vw-2rem))]">
        <DialogHeader
          eyebrow="EVIDENCE-FIRST DECISION SUPPORT"
          title="Requirement Pack & Approval Brief"
          description="Confirmed facts, commercial difference, applicable controls and prior decisions stay linked to their governed source records."
        />
        {loading && (
          <State>Building the brief from tenant-scoped source records…</State>
        )}
        {error && <State error>{error}</State>}
        {!loading && !error && brief && data && (
          <div className="grid gap-5 p-5 lg:grid-cols-[.82fr_1.18fr]">
            <section className="surface-subtle overflow-hidden">
              <header className="section-heading">
                <span className="section-heading__icon">
                  <CheckCircle2 className="size-4" />
                </span>
                <div>
                  <h2>Requirement assistance</h2>
                  <p>Each state names its rule and source.</p>
                </div>
              </header>
              <div className="divide-y divide-[var(--border)]">
                {requirements.map((item: any) => (
                  <article className="requirement-check" key={item.key}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <strong>{item.label}</strong>
                        <p>{item.reason}</p>
                      </div>
                      <Badge value={item.status} />
                    </div>
                    <div className="mt-2 flex items-center justify-between gap-3 text-xs text-[var(--text-secondary)]">
                      <span>Source: {item.source}</span>
                      {item.supported && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            onOpenChange(false);
                            onNavigate("requisitions", requisitionId);
                          }}
                        >
                          Open field <ArrowUpRight className="size-3.5" />
                        </Button>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            </section>

            <section className="surface-subtle overflow-hidden">
              <header className="section-heading">
                <span className="section-heading__icon">
                  <FileText className="size-4" />
                </span>
                <div>
                  <h2>Concise approval brief</h2>
                  <p>
                    Facts, derived summary, assumptions and missing information
                    are separated.
                  </p>
                </div>
              </header>
              <div className="grid gap-5 p-5">
                <BriefBlock label="Confirmed facts" tone="confirmed">
                  <dl className="brief-grid">
                    <Fact term="Purpose" value={brief.confirmed.purpose} />
                    <Fact
                      term="Requester"
                      value={brief.confirmed.requester || "NOT AVAILABLE"}
                    />
                    <Fact
                      term="Department / location"
                      value={brief.confirmed.location || "NOT AVAILABLE"}
                    />
                    <Fact
                      term="Need-by"
                      value={
                        brief.confirmed.needBy
                          ? displayDate(brief.confirmed.needBy)
                          : "NOT AVAILABLE"
                      }
                    />
                    <Fact
                      term="Estimated value"
                      value={
                        brief.confirmed.estimatedValue != null
                          ? money(brief.confirmed.estimatedValue)
                          : "NOT AVAILABLE"
                      }
                      numeric
                    />
                    <Fact
                      term="Current stage"
                      value={titleCase(brief.confirmed.status)}
                    />
                    <Fact
                      term="Budget"
                      value={
                        data.budget
                          ? `${data.budget.business_units?.name || data.budget.cost_category} · ${money(data.budget.allocated_amount, data.budget.currency)}`
                          : "NOT AVAILABLE"
                      }
                    />
                    <Fact
                      term="Available before request"
                      value={
                        availableBudget !== null
                          ? money(availableBudget, data.budget.currency)
                          : "NOT AVAILABLE"
                      }
                      numeric
                    />
                    <Fact
                      term="Budget variance after request"
                      value={
                        availableBudget !== null
                          ? money(availableBudget - Number(brief.confirmed.estimatedValue || 0), data.budget.currency)
                          : "NOT AVAILABLE"
                      }
                      numeric
                    />
                  </dl>
                  <p className="brief-copy">
                    <strong>Business need:</strong>{" "}
                    {brief.confirmed.businessNeed || "NOT AVAILABLE"}
                  </p>
                  <div className="hidden md:block">
                  <div className="compact-table-wrap">
                    <table className="compact-table">
                      <thead>
                        <tr>
                          <th>Item or service</th>
                          <th>Specification</th>
                          <th className="numeric">Quantity</th>
                          <th className="numeric">Estimate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {brief.confirmed.lines.map((line: any) => (
                          <tr key={line.id}>
                            <td>{line.name}</td>
                            <td>{line.specification}</td>
                            <td className="numeric">
                              {line.quantity} {line.unit}
                            </td>
                            <td className="numeric">
                              {line.estimated_unit_price !== null &&
                              line.estimated_unit_price !== undefined &&
                              line.estimated_unit_price !== ""
                                ? money(
                                    Number(line.quantity) *
                                      Number(line.estimated_unit_price),
                                  )
                                : "NOT AVAILABLE"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  </div>
                  <div className="grid gap-2 md:hidden" aria-label="Requested items">
                    {brief.confirmed.lines.map((line: any) => (
                      <article key={line.id} className="rounded-xl border border-[var(--border)] p-3 text-sm">
                        <strong>{line.name}</strong>
                        <p className="mt-1 text-[var(--text-secondary)]">{line.specification}</p>
                        <dl className="mt-3 grid grid-cols-2 gap-3">
                          <Fact term="Quantity" value={`${line.quantity} ${line.unit}`} />
                          <Fact
                            term="Estimate"
                            value={
                              line.estimated_unit_price !== null &&
                              line.estimated_unit_price !== undefined &&
                              line.estimated_unit_price !== ""
                                ? money(Number(line.quantity) * Number(line.estimated_unit_price))
                                : "NOT AVAILABLE"
                            }
                            numeric
                          />
                        </dl>
                      </article>
                    ))}
                  </div>
                </BriefBlock>

                <BriefBlock label="Derived summary" tone="derived">
                  <p className="brief-copy">
                    {brief.derived.lineCount} line item
                    {brief.derived.lineCount === 1 ? "" : "s"} ·{" "}
                    {brief.derived.approvalCount} recorded approval
                    {brief.derived.approvalCount === 1 ? "" : "s"} ·{" "}
                    {brief.derived.exceptionCount} confirmation point
                    {brief.derived.exceptionCount === 1 ? "" : "s"}.
                  </p>
                  {data.rfq ? (
                    <p className="brief-copy">
                      Sourcing: {data.rfq.rfq_no} · {data.quotes.length}{" "}
                      quotation{data.quotes.length === 1 ? "" : "s"}
                      {data.purchaseOrder
                        ? ` · awarded PO ${data.purchaseOrder.po_no}`
                        : ""}
                      .
                    </p>
                  ) : (
                    <p className="brief-copy">
                      Sourcing has not yet been recorded for this requirement.
                    </p>
                  )}
                  <div className="mt-3 grid gap-3 sm:grid-cols-3">
                    <Fact term="Selected supplier" value={selectedQuote?.vendors?.name || data.purchaseOrder?.vendors?.name || "NOT AVAILABLE"} />
                    <Fact term="L1 offer" value={lowestQuote ? `${lowestQuote.vendors?.name || "Supplier"} · ${money(lowestQuote.total, lowestQuote.currency)}` : "NOT AVAILABLE"} />
                    <Fact
                      term="Non-L1 difference"
                      value={
                        selectedVariance !== null
                          ? selectedVariance > 0
                            ? `${money(selectedVariance, selectedQuote?.currency)} above L1`
                            : "L1 selected"
                          : "NOT AVAILABLE"
                      }
                      numeric
                    />
                  </div>
                </BriefBlock>

                <BriefBlock label="Applicable controls, exceptions and risk" tone="derived">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <strong>{toneLabel(controlTone)}</strong>
                      <p className="mt-1 text-sm">
                        {evaluatedPolicy
                          ? `${evaluatedPolicy.policyName || "Organisation policy"} · version ${evaluatedPolicy.version} · effective ${displayDate(evaluatedPolicy.effective_from)}`
                          : "No evaluated organisation policy version is linked to this requisition yet."}
                      </p>
                    </div>
                    <Badge value={controlTone} />
                  </div>
                  {controlSnapshot && (
                    <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
                      <Fact term="Missing evidence" value={String(controlSnapshot.missing_count || 0)} numeric />
                      <Fact term="Open exceptions" value={String(controlSnapshot.open_exception_count || 0)} numeric />
                      <Fact term="Evidence completeness" value={`${controlSnapshot.completeness_percent || 0}%`} numeric />
                      <Fact term="Snapshot" value={`v${controlSnapshot.version} · immutable hash retained`} />
                    </dl>
                  )}
                  {(controlCase?.exceptions || []).length > 0 && (
                    <ul className="brief-list mt-3">
                      {(controlCase?.exceptions || []).map((item: any) => (
                        <li key={item.id}>
                          <AlertTriangle className="size-4" />
                          {item.reason || item.exception_type || "Controlled exception"} · {titleCase(item.status || item.state)}
                        </li>
                      ))}
                    </ul>
                  )}
                </BriefBlock>

                <BriefBlock label="Previous approval actions" tone="confirmed">
                  {data.approvals.length ? (
                    <div className="grid gap-2">
                      {data.approvals.map((approval: any) => (
                        <article key={approval.id} className="rounded-lg border border-[var(--border)] p-3 text-sm">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <strong>{approval.profiles?.full_name || "Assigned approver"}</strong>
                            <Badge value={approval.decision} />
                          </div>
                          <p className="mt-1 text-[var(--text-secondary)]">{approval.comments || "No decision note recorded."}</p>
                          <p className="mt-2 text-xs text-[var(--text-muted)]">{displayDate(approval.created_at, true)}</p>
                        </article>
                      ))}
                    </div>
                  ) : (
                    <p className="brief-copy">No approval decision has been recorded.</p>
                  )}
                </BriefBlock>

                <BriefBlock label="Assumptions" tone="assumption">
                  <p className="brief-copy">
                    Structured assumptions are unsupported by the current
                    requisition schema. RAQOZ shows this boundary instead of
                    inventing or saving an assumption.
                  </p>
                </BriefBlock>

                <BriefBlock label="Missing information" tone="missing">
                  {brief.missing.length ? (
                    <ul className="brief-list">
                      {brief.missing.map((item: any) => (
                        <li key={item.key}>
                          <AlertTriangle className="size-4" />
                          {item.label}: {item.reason}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="brief-copy">
                      No deterministic completeness gaps were found.
                    </p>
                  )}
                </BriefBlock>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="evidence-summary">
                    <Paperclip className="size-4" />
                    <strong>{data.attachments.length}</strong>
                    <span>
                      recorded attachment
                      {data.attachments.length === 1 ? "" : "s"}
                    </span>
                  </div>
                  <div className="evidence-summary">
                    <CheckCircle2 className="size-4" />
                    <strong>{data.approvals.length}</strong>
                    <span>
                      approval decision{data.approvals.length === 1 ? "" : "s"}
                    </span>
                  </div>
                </div>

                {canDecide && onDecision && onRequestClarification && (
                  <div className="grid gap-2 rounded-xl border border-[var(--border)] bg-[var(--surface-secondary)] p-4 sm:grid-cols-3">
                    <Button className="min-h-11" onClick={() => onDecision("approved")}>Approve</Button>
                    <Button className="min-h-11" variant="outline" onClick={onRequestClarification}>Request clarification</Button>
                    <Button className="min-h-11" variant="destructive" onClick={() => onDecision("rejected")}>Reject</Button>
                  </div>
                )}

                <div className="flex flex-wrap justify-end gap-2">
                  <Button variant="outline" onClick={exportDecisionPack}>
                    <FileText className="size-4" />
                    Export decision pack
                  </Button>
                  <Button variant="outline" onClick={() => onOpenChange(false)}>
                    Close brief
                  </Button>
                  <Button
                    onClick={() => {
                      onOpenChange(false);
                      onNavigate("requisitions", requisitionId);
                    }}
                  >
                    <Link2 className="size-4" />
                    Open source requisition
                  </Button>
                </div>
              </div>
            </section>
          </div>
        )}
        {!loading && !error && !brief && (
          <State>Select a requisition to build its approval brief.</State>
        )}
        <div className="border-t border-[var(--border)] bg-[var(--surface-secondary)] px-5 py-3 text-xs text-[var(--text-secondary)]">
          <Info className="mr-2 inline size-4" />
          This brief reads deterministic source evidence. Approval, rejection and clarification continue through governed server actions and remain attributable.
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BriefBlock({
  label,
  tone,
  children,
}: {
  label: string;
  tone: string;
  children: ReactNode;
}) {
  return (
    <section className={`brief-block brief-block--${tone}`}>
      <h3>{label}</h3>
      {children}
    </section>
  );
}

function Fact({
  term,
  value,
  numeric = false,
}: {
  term: string;
  value: ReactNode;
  numeric?: boolean;
}) {
  return (
    <div>
      <dt>{term}</dt>
      <dd className={numeric ? "numeric" : ""}>{value}</dd>
    </div>
  );
}

function State({
  children,
  error = false,
}: {
  children: ReactNode;
  error?: boolean;
}) {
  return (
    <div
      className={`m-5 rounded-xl border p-5 text-sm ${error ? "border-red-200 bg-red-50 text-red-800" : "border-[var(--border)] bg-[var(--surface-secondary)] text-[var(--text-secondary)]"}`}
      role={error ? "alert" : "status"}
    >
      {children}
    </div>
  );
}
